<template>
  <DatePicker
    v-bind="$attrs"
    ref="sbDatePicker"
    :model-value="sbModelValue"
    :selection-mode="sbSelectionMode"
    :placeholder="placeholder"
    :min-date="sbMinDate"
    :select-other-months="true"
    :date-format="sbDateFormat"
    :append-to="sbAppendTo"
    :panel-class="sbPanelClass"
    :show-time="sbShowTime"
    :time-only="sbTimeOnly"
    :view="sbView"
    :hide-on-range-selection="hideOnRangeSelection"
    :show-icon="true"
    :disabled="disabled"
    :hour-format="sbHourFormat"
    :disabled-dates="disabledDates"
    icon-display="input"
    :class="style.datepicker"
    data-sb-regression="sb-date-picker"
    fluid
    @update:modelValue="onUpdate"
    @show="onShow"
    @hide="onHide"
    @mousedown.stop
  >
    <template
      v-if="sbTimeOnly"
      #inputicon="slotProps"
    >
      <span
        class="pi pi-clock"
        @click="slotProps.clickCallback"
      />
    </template>
  </DatePicker>
</template>

<script setup>
import { defineProps, defineEmits, ref, useAttrs, computed, useCssModule, defineOptions } from 'vue';
import { format, parse } from 'date-fns';
import DatePicker from 'primevue/datepicker';

import { convertFromDatepickerToDateFnsPattern, convertFromDatepickerToPrimeVuePattern, convertPrimeVueToDateFnsPattern } from '@src/utils/dateFormatPatternConverter';

defineOptions({
  name: 'SbDatePicker',
});

const props = defineProps({
  /**
   * Value of the component.
   */
  modelValue: {
    type: [Date, [], null],
    default: null,
  },
  /**
   * Defines the quantity of the selection.
   * @values ['single', 'multiple', 'range']
   */
  selectionMode: {
    type: String,
    default: 'single',
  },
  /**
   * Format of the date. Defaults to PrimeVue Locale configuration.
   */
  dateFormat: {
    type: String,
    default: 'mm/dd/yy',
  },
  /**
   * Placeholder text for the input.
   */
  placeholder: {
    type: String,
    default: null,
  },
  /**
   * The minimum selectable date.
   */
  minDate: {
    type: Date,
    default: null,
  },
  /**
   * A valid query selector or an HTMLElement to specify where the overlay gets attached.
   * @values ['body', 'self', HTMLElement]
   */
  appendTo: {
    type: [String, HTMLElement],
    default: 'self',
  },
  /**
   * Style class of the overlay panel.
   */
  panelClass: {
    type: String,
    default: null,
  },
  /**
   * Type of view to display.
   * @values ['date', 'month', 'year']
   */
  view: {
    type: String,
    default: 'date',
  },
  /**
   * Whether to display timepicker.
   */
  showTime: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether to display timepicker only.
   */
  timeOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether to hide the overlay on date selection is completed when selectionMode is range.
   */
  hideOnRangeSelection: {
    type: Boolean,
    default: true,
  },
  /**
   * When enabled, displays a button with icon next to input.
   */
  showIcon: {
    type: Boolean,
    default: false,
  },
  /**
   * Array with dates to disable.
   * @type {Array<Date>}
   */
  disabledDates: {
    type: Array,
    default: null,
  },
  /**
   * When present, it specifies that the component should be disabled.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Specifies hour format.
   * @values ['12', '24']
   */
  hourFormat: {
    type: String,
    default: '24',
  },
});

const emit = defineEmits([
  /**
   * Emitted when the model value changes.
   */
  'update:modelValue',
  /**
   * Emitted when the user input is changed
   */
  'change',
  /**
   * Callback to invoke when datepicker panel is shown.
   */
  'open',
  /**
   * Callback to invoke when datepicker panel is shown.
   */
  'show',
  /**
   * Callback to invoke when datepicker panel is hidden.
   */
  'hide',
]);

const attrs = useAttrs();

// // Data
const sbDatePicker = ref(null);
const style = useCssModule();

// Computed
const sbModelValue = computed(() => {
  if (!attrs.date) return props.modelValue;

  let dateFormatPattern = attrs.type !== 'time' ? convertPrimeVueToDateFnsPattern(props.dateFormat) : null;

  if (attrs.format) {
    dateFormatPattern = convertFromDatepickerToDateFnsPattern(attrs.format);
  }

  const parseModelValue = (inputDate) => {
    if (!inputDate) return null;
    const referenceDate = new Date();
    const dateTimeString = attrs.type === 'time'
      ? `${format(referenceDate, 'yyyy-MM-dd')} ${inputDate}`
      : inputDate;

    const dateTimeStringPattern = attrs.type === 'time'
      ? `yyyy-MM-dd ${dateFormatPattern || 'HH:mm:ss'}`
      : dateFormatPattern;

    const result = parse(dateTimeString, dateTimeStringPattern, referenceDate);
    return result;
  };

  return Array.isArray(attrs.date) ? attrs.date.map(parseModelValue) : parseModelValue(attrs.date);
});

const sbSelectionMode = computed(() => {
  if (attrs.range != null) return 'range';
  return props.selectionMode;
});

const sbMinDate = computed(() => {
  if (attrs.futureOnly || attrs['future-only']) return new Date();
  return props.minDate;
});

const sbDateFormat = computed(() => {
  if (attrs.format != null && attrs.type !== 'time') {
    return convertFromDatepickerToPrimeVuePattern(attrs.format);
  }
  return props.dateFormat;
});

const sbAppendTo = computed(() => {
  if (attrs.appendToBody != null || attrs['append-to-body'] != null) {
    return attrs.appendToBody === false || attrs['append-to-body'] === false ? 'self' : 'body';
  }
  return props.appendTo;
});

const sbPanelClass = computed(() => {
  if (attrs.popupClass != null || attrs['popup-class'] != null) return attrs.popupClass || attrs['popup-class'];
  return props.panelClass;
});

const sbView = computed(() => {
  if (attrs.type != null) {
    if (['time', 'datetime', 'week'].includes(attrs.type)) return 'date';
    return attrs.type;
  }
  return props.view;
});

const sbShowTime = computed(() => {
  if (attrs.type != null && (attrs.type === 'time' || attrs.type === 'datetime')) return true;
  return props.showTime;
});

const sbTimeOnly = computed(() => {
  if (attrs.type === 'time') return true;
  return props.timeOnly;
});

const sbHourFormat = computed(() => {
  if (attrs.use12h != null || attrs['use-12h'] !== null) {
    return attrs.use12h === false || attrs['use-12h'] === false ? '24' : '12';
  }
  return props.hourFormat;
});

// Methods
const onUpdate = (newDate) => {
  let changeEventPayload;
  let dateFormat = convertPrimeVueToDateFnsPattern(sbDateFormat.value);

  if (attrs.type === 'time' || attrs.type === 'datetime') {
    dateFormat = convertFromDatepickerToDateFnsPattern(attrs.format || 'HH:mm:ss');
  }

  if (Array.isArray(newDate)) {
    changeEventPayload = newDate.map((date) => (date ? format(date, dateFormat) : date));
  } else {
    changeEventPayload = newDate ? format(newDate, dateFormat) : newDate;
  }

  emit('update:modelValue', newDate);
  emit('change', changeEventPayload); // Deprecated - to be removed in the future
};

const onShow = async () => {
  emit('open'); // Deprecated - to be removed in the future
  emit('show');
};

const onHide = async () => {
  emit('hide');
};
</script>

<style lang="scss" module>
  .datepicker {
    width: 100%;
    align-items: center;

    :global(.p-datepicker-input-icon-container) {
      top: unset;
      margin-top: unset;
    }
  }

</style>
